import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Grid, 
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  Chip
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardCard from '../components/dashboard/DashboardCard';
import HomeIcon from '@mui/icons-material/Home';
import GroupIcon from '@mui/icons-material/Group';
import BusinessIcon from '@mui/icons-material/Business';
import EventIcon from '@mui/icons-material/Event';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import GavelIcon from '@mui/icons-material/Gavel';
import PersonIcon from '@mui/icons-material/Person';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LockIcon from '@mui/icons-material/Lock';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ImageIcon from '@mui/icons-material/Image';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import axios from '../utils/axiosConfig';

import { handleApiError } from '../utils/apiErrorHandler';

interface Competition {
  _id: string;
  name: string;
  location: string;
  startDate: string;  
  endDate: string;
  registrationOpenDate: string;
  registrationDeadline: string;
}

interface RegistrationCapacity {
  eliteTotal: number;
  eliteLimit: number | null;
  basicTotal: number;
  basicLimit: number | null;
}

interface CompetitionWithExtension extends Competition {
  hasExtension?: boolean;
  extendedDeadline?: string;
  capacity?: RegistrationCapacity;
}

const Dashboard: React.FC = () => {
  const { userRole } = useAuth();
  const [ownCompetitions, setOwnCompetitions] = useState<Competition[]>([]);
  const [openCompetitions, setOpenCompetitions] = useState<CompetitionWithExtension[]>([]);  
  const [closedCompetitions, setClosedCompetitions] = useState<Competition[]>([]);  
  const [registeredCompetitions, setRegisteredCompetitions] = useState<Competition[]>([]);
  const [hasLogo, setHasLogo] = useState<boolean | null>(null);
  
  useEffect(() => {
    // Fetch competitions data
    const fetchCompetitions = async () => {
      try {
        // 🔹 Offene Wettkämpfe laden (bereits mit Verlängerungen erweitert)
        if (userRole === 'user') {
          const openResponse = await axios.get("/api/competitions/open");
          
          // Für jeden offenen Wettkampf prüfen, ob eine Verlängerung aktiv ist
          const competitionsWithExtensions = await Promise.all(
            openResponse.data.map(async (competition: Competition) => {
              const [extensionResult, capacityResult] = await Promise.allSettled([
                axios.get(`/api/registration-extensions/check/${competition._id}/${userRole === 'user' ? 'me' : ''}`),
                axios.get(`/api/competitions/${competition._id}/registration-status`)
              ]);

              const hasExtension = extensionResult.status === 'fulfilled' ? extensionResult.value.data.hasExtension : false;
              const extendedDeadline = extensionResult.status === 'fulfilled' ? extensionResult.value.data.extendedDeadline : undefined;
              const capacity: RegistrationCapacity | undefined = capacityResult.status === 'fulfilled' ? {
                eliteTotal: capacityResult.value.data.eliteTotal,
                eliteLimit: capacityResult.value.data.eliteLimit,
                basicTotal: capacityResult.value.data.basicTotal,
                basicLimit: capacityResult.value.data.basicLimit,
              } : undefined;

              return {
                ...competition,
                hasExtension,
                extendedDeadline,
                capacity
              };
            })
          );
          
          setOpenCompetitions(competitionsWithExtensions);

          // 🔹 Geschlossene Wettkämpfe laden (bereits ohne Verlängerungen gefiltert)
          axios.get("/api/competitions/closed")
            .then((response) => setClosedCompetitions(response.data))
            .catch((error) => handleApiError(error, "Fehler beim Laden der geschlossenen Wettkämpfe."));

          // 🔹 Gemeldete Wettkämpfe laden
          axios.get("/api/competitions/registered")
            .then((response) => setRegisteredCompetitions(response.data))
            .catch((error) => handleApiError(error, "Fehler beim Laden der registrierten Wettkämpfe."));
        }

        // 🔹 Eigene Wettkämpfe laden (für Admin, Organizer, Helper)
        if (userRole === 'admin' || userRole === 'organizer' || userRole === 'helper') {
          axios.get("/api/competitions/own-competitions")
            .then((response) => setOwnCompetitions(response.data))
            .catch((error) => handleApiError(error, "Fehler beim Laden der eigenen Wettkämpfe."));
        }
        
      } catch (error) {
        handleApiError(error, 'Fehler beim Laden der Wettkämpfe');
      }
    };
    
    // Fetch club logo status
    const fetchClubLogoStatus = async () => {
      if (userRole === 'user') {
        try {
          const response = await axios.get("/api/clubs/logo-status");
          setHasLogo(response.data.hasLogo);
        } catch (error) {
          handleApiError(error, 'Fehler beim Laden des Logos.');
          setHasLogo(false);
        }
      }
    };
    
    fetchCompetitions();
    fetchClubLogoStatus();
  }, [userRole]);
  
  const getCapacityChips = (capacity?: RegistrationCapacity) => {
    if (!capacity) return null;
    const chips: React.ReactNode[] = [];

    const buildChip = (total: number, limit: number | null, label: string) => {
      if (limit === null || limit <= 0) return null;
      const pct = total / limit;
      if (pct >= 1) {
        return (
          <Chip
            key={label}
            label={`${label} ausgebucht`}
            size="small"
            sx={{ bgcolor: '#d32f2f', color: 'white', fontWeight: 'bold' }}
          />
        );
      } else if (pct >= 0.8) {
        return (
          <Chip
            key={label}
            label={`>80% ${label} gebucht`}
            size="small"
            sx={{ bgcolor: '#f57c00', color: 'white', fontWeight: 'bold' }}
          />
        );
      } else if (pct >= 0.5) {
        return (
          <Chip
            key={label}
            label={`>50% ${label} gebucht`}
            size="small"
            sx={{ bgcolor: '#fff9c4', color: '#5d4037', fontWeight: 'bold' }}
          />
        );
      }
      return null;
    };

    const eliteChip = buildChip(capacity.eliteTotal, capacity.eliteLimit, 'Elite');
    const basisChip = buildChip(capacity.basicTotal, capacity.basicLimit, 'Basis');
    if (eliteChip) chips.push(eliteChip);
    if (basisChip) chips.push(basisChip);
    return chips.length > 0 ? chips : null;
  };

  // Render competitions list with extension information
  const renderCompetitionsList = (
    competitions: CompetitionWithExtension[], 
    emptyMessage: string, 
    closed: boolean = true, 
    registered: boolean = false
  ) => {
    if (competitions.length === 0) {
      return (
        <Typography variant="body1" color="text.secondary" sx={{ p: 2 }}>
          {emptyMessage} 
        </Typography>
      );
    }
    
    return (
      <List sx={{ p: 0 }}>
        {competitions
          .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
          .map(competition => {
          const deadlineToShow = competition.hasExtension && competition.extendedDeadline 
            ? competition.extendedDeadline 
            : competition.registrationDeadline;
            
          return (
            <ListItem 
              key={competition._id}
              component={Link as any}
              to={closed  
                ? (registered ?
                  `/registered-competition/${competition._id}/summary` 
                : `/competition-registration/${competition._id}/summary`) 
                : `/competition-registration/${competition._id}/athletes`}
              sx={{ 
                p: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                textDecoration: 'none',
                color: 'inherit',
                '&:hover': {
                  bgcolor: 'rgba(25, 118, 210, 0.08)'
                }
              }}
            >
              <ListItemIcon>
                <EventIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {competition.name}
                    {competition.hasExtension && (
                      <Chip
                        icon={<AccessTimeIcon />}
                        label="Verlängert"
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    )}
                    {getCapacityChips(competition.capacity)}
                  </Box>
                }
                secondary={
                  <>
                    {`${competition.location} | Datum: ${new Date(competition.startDate).toLocaleDateString()} - ${new Date(competition.endDate).toLocaleDateString()} | Meldestart: ${new Date(competition.registrationOpenDate).toLocaleDateString()}`}
                    <br />
                    {competition.hasExtension ? (
                      <>
                        <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>
                          Meldeschluss: {new Date(competition.registrationDeadline).toLocaleDateString()}
                        </span>
                        {' → '}
                        <strong>Verlängert bis: {new Date(deadlineToShow).toLocaleDateString()}</strong>
                      </>
                    ) : (
                      `Meldeschluss: ${new Date(deadlineToShow).toLocaleDateString()}`
                    )}
                  </>
                }
              />
            </ListItem>
          );
        })}
      </List>
    );
  };

  // Render competitions list for admin/organizer/helper (unchanged)
  const renderOwnCompetitionsList = (competitions: Competition[], emptyMessage: string) => {
    if (competitions.length === 0) {
      return (
        <Typography variant="body1" color="text.secondary" sx={{ p: 2 }}>
          {emptyMessage}
        </Typography>
      );
    }
    
    return (
      <List sx={{ p: 0 }}>
        {competitions
          .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
          .map(competition => (
          <ListItem 
            key={competition._id}
            component={Link as any}
            to={`/orga/competition/${competition._id}`}
            sx={{ 
              p: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              textDecoration: 'none',
              color: 'inherit',
              '&:hover': {
                bgcolor: 'rgba(25, 118, 210, 0.08)'
              }
            }}
          >
            <ListItemIcon>
              <EventIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary={competition.name}
              secondary={`${competition.location} | Datum: ${new Date(competition.startDate).toLocaleDateString()} - ${new Date(competition.endDate).toLocaleDateString()} | Meldestart: ${new Date(competition.registrationOpenDate).toLocaleDateString()} | Meldeschluss: ${new Date(competition.registrationDeadline).toLocaleDateString()}`}
            />
          </ListItem>
        ))}
      </List>
    );
  };
  
  // Admin Dashboard Content
  const renderAdminDashboard = () => {
    // Bestimmen, welcher Dashboard-Titel angezeigt wird basierend auf der Rolle
    let dashboardTitle = 'Willkommen im Admin-Dashboard';
    if (userRole === 'organizer') {
      dashboardTitle = 'Willkommen im Organisator-Dashboard';
    } else if (userRole === 'helper') {
      dashboardTitle = 'Willkommen im Helfer-Dashboard';
    }

    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 3, 
              mb: 3, 
              borderRadius: 2,
              bgcolor: 'primary.light', 
              color: 'white'
            }}
          >
            <Typography variant="h4" gutterBottom fontWeight="500">
              {dashboardTitle}
            </Typography>
            <Typography variant="body1">
              Hier kannst Du alle Aspekte des Wettkampfportals verwalten.
            </Typography>
          </Paper>
        </Grid>
        
        {/* Vereine verwalten - nur für Admins */}
        {userRole === 'admin' && (
          <Grid item xs={12} sm={6} md={4}>
            <DashboardCard
              title="Vereine verwalten"
              icon={<BusinessIcon />}
              items={[
                { id: 'clubs-list', text: 'Vereinsliste anzeigen', path: '/admin/clubs', icon: <GroupIcon /> }
              ]}
            />
          </Grid>
        )}
        
        {/* Benutzer verwalten - nur für Admins */}
        {userRole === 'admin' && (
          <Grid item xs={12} sm={6} md={4}>
            <DashboardCard
              title="Benutzer verwalten"
              icon={<ManageAccountsIcon />}
              items={[
                { id: 'users-list', text: 'Benutzerliste anzeigen', path: '/admin/users', icon: <PersonIcon /> }
              ]}
            />
          </Grid>
        )}
        
        {/* Wettkämpfe verwalten - für Admins und Organisatoren */}
        {(userRole === 'admin' || userRole === 'organizer') && (
          <Grid item xs={12} sm={6} md={4}>
            <DashboardCard
              title="Wettkämpfe verwalten"
              icon={<EventIcon />}
              items={[
                { id: 'competitions-list', text: 'Wettkampfliste anzeigen', path: '/admin/competitions', icon: <EventIcon /> },
                { id: 'competition-create', text: 'Neuen Wettkampf anlegen', path: '/admin/competitions/new', icon: <LocalActivityIcon /> }
              ]}
            />
          </Grid>
        )}
        
        {/* Profil-Karte - für alle Admin-Rollen */}
        <Grid item xs={12} sm={6} md={4}>
          <DashboardCard
            title="Mein Profil"
            icon={<AccountBoxIcon />}
            items={[
              { id: 'profile', text: 'Profildaten bearbeiten', path: '/club/profile', icon: <EditNoteIcon /> }
            ]}
          />
        </Grid>
        
        {/* Your Competitions - für Admin, Organizer, Helper */}
        <Grid item xs={12}>
          <Paper 
            elevation={2} 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <Box sx={{ 
              p: 2, 
              bgcolor: 'primary.main', 
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}>
              <CalendarTodayIcon />
              <Typography variant="h6">Deine Wettkämpfe</Typography>
            </Box>
            {renderOwnCompetitionsList(
              ownCompetitions, 
              'Keine eigenen Wettkämpfe vorhanden.'
            )}
          </Paper>
        </Grid>
        
        {/* Wettkampfkarte */}
        {/*<Grid item xs={12} sm={6} md={4}>
          <DashboardCard
            title="Wettkampfkarte"
            icon={<BusinessIcon />}
            items={[
              { id: 'clubs-list', text: 'Wettkampfkarte', path: '/trampoline-scorecard', icon: <GroupIcon /> }
            ]}
          />
        </Grid>*/}
      </Grid>
    );
  };
  
  // User Dashboard Content
  const renderUserDashboard = () => {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 3, 
              mb: 3, 
              borderRadius: 2,
              bgcolor: 'primary.light', 
              color: 'white'
            }}
          >
            <Typography variant="h4" gutterBottom fontWeight="500">
              Willkommen in Deinem Vereinsportal
            </Typography>
            <Typography variant="body1">
              Hier kannst Du Deine Vereinsdaten verwalten und Wettkampfmeldungen durchführen.
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
        <DashboardCard
          title="Vereinsverwaltung"
          icon={<BusinessIcon />}
          items={[
            { id: 'club-profile', text: 'Vereinsdaten', path: '/club/profile', icon: <EditNoteIcon /> },
            { id: 'athletes', text: 'Sportler verwalten', path: '/club/athletes', icon: <SportsSoccerIcon /> },
            { id: 'judges', text: 'Kampfrichter verwalten', path: '/club/judges', icon: <GavelIcon /> },
            { id: 'officials', text: 'Offizielle verwalten', path: '/club/officials', icon: <PersonIcon /> }
          ]}
          alertContent={
            hasLogo === false && (
              <Alert 
                severity="warning" 
                icon={false}
                sx={{ borderRadius: 0 }}
              >
                Du hast noch kein Vereinslogo hochgeladen.
              </Alert>
            )
          }
        />
        </Grid>
        
        {/* Open Competitions */}
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={2} 
            sx={{ 
              borderRadius: 2,
              overflow: 'hidden',
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box sx={{ 
              p: 2, 
              bgcolor: 'primary.main', 
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}>
              <EventIcon />
              <Typography variant="h6">Offene Wettkämpfe</Typography>
            </Box>
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              {renderCompetitionsList(
                openCompetitions, 
                'Derzeit sind keine offenen Wettkämpfe verfügbar.',
                false
              )}
            </Box>
          </Paper>
        </Grid>
        
        {/* Closed Competitions */}
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={2} 
            sx={{ 
              borderRadius: 2,
              overflow: 'hidden',
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box sx={{ 
              p: 2, 
              bgcolor: 'primary.main', 
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}>
              <LockIcon />
              <Typography variant="h6">Gemeldete Wettkämpfe</Typography>
            </Box>
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              {renderCompetitionsList(
                registeredCompetitions, 
                'Keine gemeldeten Wettkämpfe vorhanden.',
                true, true
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );
  };
  
  return (
    <Box>
      {(userRole === 'admin' || userRole === 'organizer' || userRole === 'helper') 
        ? renderAdminDashboard() 
        : renderUserDashboard()}
    </Box>
  );
};

export default Dashboard;